{/* TODO: Frank to finalize copy before launch — current text is a generic placeholder */}
import { PublicNav } from "@/components/website/PublicNav";
import { PublicFooter } from "@/components/website/PublicFooter";
import { PublicLayout } from "@/components/website/PublicLayout";
import { useAppSettings } from "@/hooks/useAppSettings";

export default function PrivacyPage() {
  const { data: settings } = useAppSettings();
  const businessName = settings?.site_logo_text
    ? `${settings.site_logo_text} ${settings.site_logo_subtitle ?? ""}`.trim()
    : "Tristano's Catering";
  const email = settings?.business_email || "info@tristanoscatering.ca";
  const lastUpdated = "April 2026";

  return (
    <PublicLayout className="min-h-screen bg-background flex flex-col">
      <PublicNav />

      <main id="main-content" className="flex-1">
        {/* Hero */}
        <div className="bg-primary py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-logo font-bold text-primary-foreground">
              Privacy Policy
            </h1>
            <p className="mt-2 text-primary-foreground/80 text-sm font-secondary">
              Last updated: {lastUpdated}
            </p>
          </div>
        </div>

        {/* Body */}
        <article className="max-w-3xl mx-auto px-4 py-10 md:py-14 font-secondary text-foreground/90 space-y-6 leading-relaxed">
          <p>
            {businessName} (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) is
            committed to protecting the personal information you share with us. This Privacy
            Policy explains what information we collect, how we use it, and the choices you
            have. It is intended to align with Canada&rsquo;s Personal Information Protection
            and Electronic Documents Act (PIPEDA).
          </p>

          <section className="space-y-3">
            <h2 className="text-xl md:text-2xl font-logo font-semibold text-foreground">
              1. Information we collect
            </h2>
            <p>We collect personal information that you provide directly, including:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Contact details such as your name, email address, and phone number</li>
              <li>Event and order details (date, guest count, menu preferences, delivery address)</li>
              <li>Payment details processed by our payment partners (we do not store full card numbers)</li>
              <li>Messages you send us through our website, email, or phone</li>
            </ul>
            <p>
              We also collect limited technical information automatically, such as your IP
              address, device type, browser, and pages viewed, primarily for analytics,
              security, and to keep the site working properly.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl md:text-2xl font-logo font-semibold text-foreground">
              2. How we use your information
            </h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>To respond to quote requests, bookings, and customer support inquiries</li>
              <li>To prepare, deliver, and invoice your catering order</li>
              <li>To send transactional messages (order confirmations, receipts, reminders)</li>
              <li>To improve our menus, pricing, and website experience</li>
              <li>To comply with our legal obligations and protect our legitimate business interests</li>
            </ul>
            <p>
              We will only use your information for purposes you would reasonably expect, and
              we will ask for your consent for anything beyond that.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl md:text-2xl font-logo font-semibold text-foreground">
              3. Sharing with service providers
            </h2>
            <p>
              We share limited personal information with trusted third parties that help us
              run our business, such as payment processors, email delivery providers,
              analytics tools, and our customer relationship management (CRM) system. These
              providers may process your data in Canada, the United States, or other
              countries. We require them to safeguard your information and to only use it for
              the services they provide to us.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl md:text-2xl font-logo font-semibold text-foreground">
              4. How we protect your information
            </h2>
            <p>
              We use reasonable physical, technical, and administrative safeguards to protect
              personal information against loss, theft, and unauthorized access, including
              encrypted connections, access controls, and regular backups.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl md:text-2xl font-logo font-semibold text-foreground">
              5. How long we keep it
            </h2>
            <p>
              We keep personal information only as long as necessary to provide the services
              you&rsquo;ve asked for and to meet our legal and accounting obligations. When it
              is no longer needed, we securely delete or anonymize it.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl md:text-2xl font-logo font-semibold text-foreground">
              6. Your rights
            </h2>
            <p>
              Under PIPEDA you have the right to access and correct the personal information
              we hold about you, and to withdraw consent for future use (subject to legal or
              contractual limits). To exercise these rights, or to ask a question about this
              policy, contact us at{" "}
              <a className="underline hover:text-primary" href={`mailto:${email}`}>
                {email}
              </a>
              .
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl md:text-2xl font-logo font-semibold text-foreground">
              7. Changes to this policy
            </h2>
            <p>
              We may update this policy from time to time. When we do, we&rsquo;ll change the
              &ldquo;Last updated&rdquo; date at the top of this page. For significant changes
              we&rsquo;ll also notify you by a notice on our website or by email where
              appropriate.
            </p>
          </section>
        </article>
      </main>

      <PublicFooter />
    </PublicLayout>
  );
}
